import { Button, createSellarColumnHelper } from "@sellar-io/components";
import { StockOverviewListResponseType } from "@sellar-io/api-v2/types";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";

import { useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useShallow } from "zustand/react/shallow";
import { useStockOverviewStore } from "../StockOverviewStore";
import { Icon, ProfilePicture } from "../../../components";
import { StockOverviewSortType } from "../StockOverviewFilterTypes";
import { AvailableFromBadge } from "../../../components/badge/AvailableFromBadge";
import {
  Dropdown,
  DropdownButton,
  DropdownHeadingItem,
  DropdownItem,
  DropdownItems,
} from "../../../components/dropdown/Dropdown";
import { StockStatusIcon } from "../../product/list/components/StockStatusCard";
import { InfoBadge } from "../../../components/badge/InfoBadge";
import { Link } from "react-router";
import { PRODUCT_LIST_CATEGORY } from "../../../constants/routes";
import { TableInfinite } from "../../../components/table/TableInfinite";
import { useSupplierProduct } from "../../../hooks/api/v2/product/useSupplierProduct";
import { ItemVisibilityDialog } from "../../product/list/v2/ProductCategory/ProductCategoryItemV2/ItemVisibilityDialog";

type StockOverviewListResponseElement =
  StockOverviewListResponseType["data"][number];

interface ColumnDataType extends StockOverviewListResponseElement {
  isOutOfStock: boolean;
  isHidden: boolean;
}

const StockOverviewAvailableCellContent = ({
  canBackorder,
  availableStock,
  active,
}: {
  canBackorder: boolean;
  availableStock: number;
  active: boolean;
}) => {
  const stockAmount = useMemo(() => {
    if (canBackorder) return <div>Available</div>;
    if (availableStock <= 0)
      return <div className="text-nowrap text-red-800">Sold out</div>;
    return <div className="text-nowrap">{availableStock.toString()}</div>;
  }, [canBackorder, availableStock]);

  const isHidden = !active;
  const isInStock = canBackorder || availableStock > 0;

  return (
    <div
      className={twMerge(
        "flex w-full items-center justify-between gap-1",
        isHidden && isInStock && "opacity-50",
      )}
    >
      {stockAmount}
      <div className="flex items-center gap-1.5">
        <StockStatusIcon
          availableStock={availableStock}
          canBackorder={canBackorder}
        />
      </div>
    </div>
  );
};

const StockOverviewItemVisibilityDialogWrapper = ({
  active,
  productId,
  parentProductId,
  onDismiss,
}: {
  active: boolean;
  productId: number;
  parentProductId: number;
  onDismiss: () => void;
}) => {
  const productQuery = useSupplierProduct(parentProductId, { enabled: active });
  const product = productQuery.data?.Products.find((p) => p.id === productId);

  const [selectedProduct, setSelectedProduct] =
    useState<NonNullable<typeof product>>();
  if (active && product && !selectedProduct) {
    setSelectedProduct(product);
  }

  return (
    <ItemVisibilityDialog
      product={selectedProduct ?? null}
      active={active}
      onDismiss={onDismiss}
      afterLeave={() => setSelectedProduct(undefined)}
    />
  );
};

const StockOverviewOptionsCell = ({
  row,
  onShowProductStockManagementDialog,
  onShowProductDetailsDialog,
  onShowProductDeleteDialog,
}: {
  row: ColumnDataType;
  onShowProductStockManagementDialog: (
    product: StockOverviewListResponseElement,
  ) => void;
  onShowProductDetailsDialog: (
    product: StockOverviewListResponseElement,
  ) => void;
  onShowProductDeleteDialog: (
    product: StockOverviewListResponseElement,
  ) => void;
}) => {
  const [visibilityDialogOpen, setVisibilityDialogOpen] = useState(false);

  return (
    <div className="flex w-full justify-center">
      <div className={"text-large flex items-center gap-2"}>
        <Dropdown>
          <DropdownButton>
            <Button size={"small"} color={"transparent"}>
              <Icon icon={"DOTS_VERTICAL"} className="text-black" />
            </Button>
          </DropdownButton>
          <DropdownItems>
            <DropdownHeadingItem className="text-sm">Edit</DropdownHeadingItem>
            <DropdownItem
              className="border-b border-zinc-200 text-sm"
              onClick={() => onShowProductDetailsDialog(row)}
            >
              Item details
            </DropdownItem>
            <DropdownItem
              className="border-b border-zinc-200 text-sm"
              onClick={() => onShowProductStockManagementDialog(row)}
            >
              Stock management
            </DropdownItem>
            {row.Parent?.id && (
              <DropdownItem
                className="border-b border-zinc-200 text-sm"
                onClick={() => setVisibilityDialogOpen(true)}
              >
                Item visibility
              </DropdownItem>
            )}
            <DropdownItem
              className="border-b border-zinc-200 text-sm text-red-700"
              onClick={() => onShowProductDeleteDialog(row)}
            >
              Delete item
            </DropdownItem>
          </DropdownItems>
        </Dropdown>
      </div>
      {row.Parent?.id && (
        <StockOverviewItemVisibilityDialogWrapper
          active={visibilityDialogOpen}
          productId={row.id}
          parentProductId={row.Parent.id}
          onDismiss={() => setVisibilityDialogOpen(false)}
        />
      )}
    </div>
  );
};

export const StockOverviewTable = ({
  stockOverviewQuery,
  handleTableSort,
  onShowProductStockManagementDialog,
  onShowProductDetailsDialog,
  onShowProductDeleteDialog,
}: {
  stockOverviewQuery: UseInfiniteQueryResult<
    InfiniteData<StockOverviewListResponseType>
  >;
  handleTableSort: (value: StockOverviewSortType | null) => void;
  onShowProductStockManagementDialog: (
    product: StockOverviewListResponseType["data"][number],
  ) => void;
  onShowProductDetailsDialog: (
    product: StockOverviewListResponseType["data"][number],
  ) => void;
  onShowProductDeleteDialog: (
    product: StockOverviewListResponseType["data"][number],
  ) => void;
}) => {
  const { data, fetchNextPage, isFetching } = stockOverviewQuery;

  const totalResultCount = stockOverviewQuery.data?.pages[0]?.meta.count ?? 0;

  const flatData = useMemo(
    () =>
      data?.pages
        .flatMap((page) => page.data)
        .map((item) => ({
          ...item,
          isHidden: !item.active || item.activeProductAvailabilityTiers === 0,
          isOutOfStock: item.availableStock <= 0 && !item.canBackorder,
        })) ?? [],
    [data],
  );

  const { columnSort } = useStockOverviewStore(
    useShallow((state) => ({
      columnSort: state.columnSort,
    })),
  );

  const columnHelper = createSellarColumnHelper<ColumnDataType>();

  const columns = [
    columnHelper.accessor((row) => row, {
      id: "sku",
      header: "SKU",
      cell: (info) => {
        const productItem = info.getValue();
        const parentProductInactive = !productItem.Parent?.active;
        return (
          <div
            className={twMerge(
              "flex flex-col gap-1 overflow-hidden",
              (productItem.isHidden || productItem.isOutOfStock) &&
                "opacity-50",
            )}
          >
            {parentProductInactive && (
              <div className="text-red-500">Inactive</div>
            )}
            <div className="truncate">{productItem.sku ?? "-"}</div>
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "parent_name",
      header: "Name",
      cell: (info) => {
        const productItem = info.getValue();
        const parentProduct = productItem.Parent;
        const availableFrom = productItem.availableFrom;

        const v = productItem.vegan ?? productItem.Parent?.vegan ?? false;
        const gf =
          productItem.glutenFree ?? productItem.Parent?.glutenFree ?? false;
        const lf =
          productItem.lactoseFree ?? productItem.Parent?.lactoseFree ?? false;

        const dietaryList = [
          ...(v ? ["VG"] : []),
          ...(gf ? ["GF"] : []),
          ...(lf ? ["LF"] : []),
        ].join(", ");

        const subTitle =
          parentProduct?.kind && parentProduct.abv
            ? `${parentProduct.kind} // ${parentProduct.abv}% ${dietaryList.length ? `• ${dietaryList}` : ""}`
            : null;

        return (
          <div
            className={twMerge(
              "flex w-full items-center gap-1",
              (productItem.isHidden || productItem.isOutOfStock) &&
                "opacity-50",
            )}
          >
            {/* product picture, title and subtitle */}

            <div className="flex min-w-0 flex-1 items-center gap-2">
              <ProfilePicture
                logoUrl={parentProduct?.heroImageUrl}
                size={"medium"}
              />
              <div className="overflow-hidden">
                <Link
                  className="group flex cursor-pointer font-medium hover:font-semibold"
                  to={
                    parentProduct?.id
                      ? `${PRODUCT_LIST_CATEGORY.href({ productId: parentProduct.id })}?tab=${parentProduct.active ? "Active" : "Inactive"}`
                      : ""
                  }
                >
                  <span className="truncate group-hover:underline">
                    {parentProduct?.name}
                  </span>
                  <Icon
                    className="group-hover invisible flex-none group-hover:visible"
                    icon={"CHEVRON_RIGHT"}
                  />
                </Link>
                {!!subTitle && <div className="text-xs">{subTitle}</div>}
              </div>
            </div>
            <div className="flex-shrink-0">
              {!!availableFrom && (
                <AvailableFromBadge availableFrom={availableFrom} />
              )}
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row, {
      header: "Container",
      enableSorting: false,
      cell: (info) => {
        const productItem = info.getValue();
        const containerTypeDetails = productItem.name?.split("•")[1] ?? null;
        return (
          <div
            className={twMerge(
              (productItem.isHidden || productItem.isOutOfStock) &&
                "opacity-50",
            )}
          >
            {containerTypeDetails ?? "-"}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "incoming",
      header: () => {
        return (
          <div className="flex w-full items-center gap-2">
            Incoming
            <InfoBadge
              infoContent={{
                title: "Incoming",
                body: "This is stock you’ve ring-fenced for pre-order.",
              }}
            />
          </div>
        );
      },
      enableSorting: false,
      cell: (info) => {
        const productItem = info.getValue();
        return (
          <div
            className={twMerge(
              (productItem.isHidden || productItem.isOutOfStock) &&
                "opacity-50",
            )}
          >
            {productItem.incomingStock}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row, {
      header: "Total (on hand)",
      enableSorting: false,
      cell: (info) => {
        const productItem = info.getValue();
        return (
          <div
            className={twMerge(
              (productItem.isHidden || productItem.isOutOfStock) &&
                "opacity-50",
            )}
          >
            {productItem.stock}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "committed",
      header: () => {
        return (
          <div className="flex w-full items-center gap-2">
            Committed
            <InfoBadge
              infoContent={{
                title: "Committed",
                body: "This is the stock that’s been ordered but not yet finalised.",
              }}
            />
          </div>
        );
      },
      enableSorting: false,
      cell: (info) => {
        const productItem = info.getValue();
        return (
          <div
            className={twMerge(
              (productItem.isHidden || productItem.isOutOfStock) &&
                "opacity-50",
            )}
          >
            {productItem.committedStock}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "available_stock",
      header: "Available",
      cell: (info) => {
        const productItem = info.getValue();
        const { availableStock, canBackorder, active } = productItem;
        return (
          <StockOverviewAvailableCellContent
            canBackorder={canBackorder}
            availableStock={availableStock}
            active={active}
          />
        );
      },
    }),

    columnHelper.accessor((row) => row, {
      id: "options",
      enableSorting: false,
      enableResizing: true,
      header: "",
      cell: (row) => (
        <StockOverviewOptionsCell
          row={row.getValue()}
          onShowProductDetailsDialog={onShowProductDetailsDialog}
          onShowProductStockManagementDialog={
            onShowProductStockManagementDialog
          }
          onShowProductDeleteDialog={onShowProductDeleteDialog}
        />
      ),
    }),
  ];

  const gridClasses =
    "grid-cols-[minmax(100px,_1fr)_minmax(450px,_1fr)_minmax(150px,_1fr)_1fr_1fr_1fr_1fr_60px]";

  return (
    <TableInfinite
      columns={columns}
      data={flatData}
      infiniteQuery={{
        data: flatData,
        fetchNextPage,
        isFetching,
        totalCount: totalResultCount,
      }}
      columnSortName={columnSort}
      handleTableSort={handleTableSort}
      gridClasses={gridClasses}
    />
  );
};
